import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import useIsMobile from '../../../hooks/useIsMobile';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Phone formatting function
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

const emptyPoc = {
  name: '',
  email: '',
  phone: '',
  title: '',
  is_primary: false,
  notes: '',
};

export default function PocFormDialog({ open, onClose, onSave, poc, loading, clientId }) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const [formData, setFormData] = React.useState(emptyPoc);
  const [errors, setErrors] = React.useState({});

  React.useEffect(() => {
    if (poc) {
      setFormData({
        name: poc.name || '',
        email: poc.email || '',
        phone: poc.phone || '',
        title: poc.title || '',
        is_primary: poc.is_primary || false,
        notes: poc.notes || '',
      });
    } else {
      setFormData(emptyPoc);
    }
    setErrors({});
  }, [poc, open]);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.trim().length < 2 || formData.name.trim().length > 255) {
      newErrors.name = 'Name must be between 2 and 255 characters';
    }
    
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && formData.phone.trim()) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 6 || phoneDigits.length > 128) {
        newErrors.phone = 'Phone must be between 6 and 128 characters';
      }
    }
    
    if (formData.title && formData.title.trim().length > 255) {
      newErrors.title = 'Title must be at most 255 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave({
        ...formData,
        name: formData.name.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        title: formData.title?.trim() || null,
        notes: formData.notes?.trim() || null,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            maxHeight: isSmallScreen ? '100%' : '90vh',
            m: isSmallScreen ? 0 : undefined,
          }
        }
      }}
    >
      <Box 
        id="draggable-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper',
          cursor: isSmallScreen ? 'default' : 'move',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: 'text.primary',
          fontSize: '1.25rem',
          fontFamily: 'inherit'
        }}>
          {poc ? 'Edit POC' : 'Add POC'}
        </Typography>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Name */}
          <TextField 
            fullWidth 
            label="Name *" 
            variant="outlined" 
            value={formData.name || ''}
            error={!!errors.name}
            helperText={errors.name}
            onChange={(event) => setFormData({ ...formData, name: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Email */}
          <TextField 
            fullWidth 
            label="Email" 
            variant="outlined" 
            value={formData.email || ''}
            error={!!errors.email}
            helperText={errors.email}
            onChange={(event) => setFormData({ ...formData, email: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Phone */}
          <TextField 
            fullWidth 
            label="Phone" 
            variant="outlined" 
            value={formData.phone || ''}
            error={!!errors.phone}
            helperText={errors.phone}
            onChange={(event) => {
              const formattedPhone = formatPhoneNumber(event.target.value);
              setFormData({ ...formData, phone: formattedPhone });
            }}
            placeholder="(555) 123-4567"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Title */}
          <TextField 
            fullWidth 
            label="Title" 
            variant="outlined" 
            value={formData.title || ''}
            error={!!errors.title}
            helperText={errors.title || 'e.g., Project Manager, Operations Manager'}
            onChange={(event) => setFormData({ ...formData, title: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Is Primary */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_primary || false}
                onChange={(event) => setFormData({ ...formData, is_primary: event.target.checked })}
                sx={{
                  color: isDarkMode ? '#fff' : '#000',
                  '&.Mui-checked': {
                    color: isDarkMode ? '#fff' : '#000',
                  }
                }}
              />
            }
            label="Primary POC"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                color: 'text.primary',
              }
            }}
          />

          {/* Notes */}
          <TextField 
            fullWidth 
            label="Notes" 
            variant="outlined" 
            multiline
            rows={3}
            value={formData.notes || ''}
            onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              }
            }}
          />
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        backgroundColor: 'background.paper',
      }}>
        <Button 
          variant="outlined"
          onClick={onClose}
          sx={{ 
            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            color: 'text.primary',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            '&:hover': {
              borderColor: isDarkMode ? '#475569' : '#bdbdbd',
              backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
            }
          }}
        >
          Cancel
        </Button>
        
        <Button 
          disabled={loading} 
          variant="contained" 
          onClick={handleSave}
          sx={{ 
            backgroundColor: isDarkMode ? '#fff' : '#000',
            color: isDarkMode ? '#000' : 'white',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: isDarkMode ? '#475569' : '#666',
              color: isDarkMode ? '#94a3b8' : '#fff',
            }
          }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Dialog>
  );
}

